import {
  AspectRatio,
  BREAKPOINTS,
  ImageSource,
  ImageSourceSizes,
  setQueryParams,
  type AspectRatioProps
} from '@ag1/ui';
import clsx, { ClassValue } from 'clsx';
import { ComponentProps, useMemo, type ReactEventHandler } from 'react';
import { SanityImage } from 'schema/studio/common/image';
import { urlFor, type PartialSanityImageAsset } from 'shared/utils/urlFor';
import { SanityImageAsset, SanityReference } from 'types/studio/studio';

export interface CustomSanityImage {
  url: SanityImage['url'];
  alt?: SanityImage['alt'];
  fit: SanityImage['fit'];
  position: SanityImage['position'];
}

type Props = {
  image: SanityImage | CustomSanityImage;
  mobileImage?: SanityImage | CustomSanityImage | null;
  tabletImage?: SanityImage | CustomSanityImage | null;
  sizes?: ImageSourceSizes;
  className?: string;
  childClassName?: ClassValue;
  eagerLoading?: boolean;
  draggable?: boolean;
  mobileFullQuality?: boolean;
  onLoad?: ReactEventHandler<HTMLElement> | undefined;
  maxDesktopWidth?: number;
  maxTabletWidth?: number;
  maxMobileWidth?: number;
} & Pick<AspectRatioProps, 'aspectRatio'>;

const DESKTOP_MAX_WIDTH_IMAGE = 1000;
const TABLET_MAX_WIDTH_IMAGE = 1000; // TODO: Revisit max widths for optimal image sizes
const MOBILE_MAX_WIDTH_IMAGE = 400;

const StudioBaseImage = ({
  image,
  mobileImage,
  tabletImage,
  sizes,
  aspectRatio,
  className,
  childClassName: childClassNameProp,
  eagerLoading = false,
  draggable = true,
  mobileFullQuality = false,
  onLoad,
  maxDesktopWidth,
  maxTabletWidth,
  maxMobileWidth
}: Props) => {
  const desktopWidth = maxDesktopWidth ?? DESKTOP_MAX_WIDTH_IMAGE;
  const tabletWidth = maxTabletWidth ?? TABLET_MAX_WIDTH_IMAGE;
  const mobileWidth = maxMobileWidth ?? MOBILE_MAX_WIDTH_IMAGE;

  // Request WebP directly from Sanity for better compression.
  const sources = useMemo<ComponentProps<typeof ImageSource>[]>(
    () => [
      {
        media: `(min-width:${BREAKPOINTS.l}px)`,
        src: setQueryParams(image.url, {
          w: desktopWidth,
          dpr: 2,
          fit: 'max',
          fm: 'png'
        })
      },
      {
        media: `(min-width:${BREAKPOINTS.s}px)`,
        src: setQueryParams(tabletImage?.url ?? image.url, {
          w: tabletWidth,
          dpr: 2,
          fit: 'max',
          fm: 'png'
        })
      },
      {
        media: `(max-width:${BREAKPOINTS.s}px)`,
        src: setQueryParams(mobileImage?.url ?? image.url, {
          w: mobileFullQuality ? desktopWidth : mobileWidth,
          dpr: 2,
          fit: 'max',
          fm: 'png'
        })
      }
    ],
    [
      image.url,
      desktopWidth,
      tabletImage?.url,
      tabletWidth,
      mobileImage?.url,
      mobileFullQuality,
      mobileWidth
    ]
  );

  return (
    <AspectRatio
      aspectRatio={aspectRatio}
      fit={image.fit}
      position={image.position}
      className={className}>
      {({ style, childClassName, containerClassName }) => (
        <picture className={containerClassName} style={style} onLoad={onLoad}>
          {sources.map((source, i) => (
            <ImageSource
              // It's completely static as an array.
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              {...source}
              sizes={sizes}
              fill
            />
          ))}

          <img
            alt={image.alt || ''}
            loading={eagerLoading ? 'eager' : 'lazy'}
            draggable={draggable}
            decoding="async"
            className={clsx(childClassName, childClassNameProp)}
            fetchPriority={eagerLoading ? 'high' : undefined}
          />
        </picture>
      )}
    </AspectRatio>
  );
};

type ImageUnion =
  | {
      asset: SanityReference<SanityImageAsset> | PartialSanityImageAsset;
      alt?: string;
    }
  | { url: string };

type ImageProp = ImageUnion & {
  [Key in keyof Pick<CustomSanityImage, 'position'>]?: CustomSanityImage[Key];
} & SupportedFitOrString;

// Typing should allow completion for supported types, but allow unofficially
// supported types to fall-through.
type SupportedFitOrString =
  | { fit?: CustomSanityImage['fit'] }
  | { fit?: Exclude<string, CustomSanityImage['fit']> };

type PropsV2 = {
  image: ImageProp;
  mobileImage?: ImageProp | null;
  tabletImage?: ImageProp | null;
  sizes?: ImageSourceSizes;
  className?: string;
  childClassName?: ClassValue;
  withMobileImage?: boolean;
  eagerLoading?: boolean;
  draggable?: boolean;
  mobileFullQuality?: boolean;
  onLoad?: ReactEventHandler<HTMLElement> | undefined;
  maxDesktopWidth?: number;
  maxTabletWidth?: number;
  maxMobileWidth?: number;
} & Pick<AspectRatioProps, 'aspectRatio'>;

export const StudioBaseImageV2 = ({
  image,
  mobileImage,
  tabletImage,
  withMobileImage = true,
  mobileFullQuality = false,
  ...props
}: PropsV2) => {
  const formatSanityImage = (img: any) => {
    if (!img) return null;

    if ('asset' in img && '_ref' in img.asset) {
      // Handle ref format
      return {
        ...img,
        ...urlFor(img) // Ensure urlFor handles references correctly
      };
    }

    if ('url' in img) {
      // Handle direct URL format
      return {
        ...img,
        url: img.url,
        ext: (img.url as string).split('.').pop()
      };
    }

    return null;
  };

  // Format main image
  const formatImage = useMemo(() => {
    return formatSanityImage(image);
  }, [image]) as unknown as SanityImage;

  // Format tablet image
  const formatTabletImage = useMemo(() => {
    if (!tabletImage || !withMobileImage) return null;
    return formatSanityImage(tabletImage);
  }, [tabletImage, withMobileImage]) as unknown as SanityImage;

  // Format mobile image
  const formatMobileImage = useMemo(() => {
    if (!mobileImage) return withMobileImage ? null : null; // Adjust based on desired fallback
    return withMobileImage ? formatSanityImage(mobileImage) : formatSanityImage(mobileImage);
  }, [mobileImage, withMobileImage]) as unknown as SanityImage;

  if (!formatImage) {
    return null;
  }

  return (
    <StudioBaseImage
      {...props}
      image={formatImage}
      tabletImage={formatTabletImage}
      mobileImage={formatMobileImage}
      mobileFullQuality={mobileFullQuality}
    />
  );
};

export default StudioBaseImage;
